import {Logger, Utils, lodash, ObjectUtils} from 'neo-common';
import {IGameClientInterceptor, InterceptorRequestContext} from './IGameClientInterceptor';
import {ApiClientCommunication} from 'neo-common';
import {SendRequestFunctionType} from './Types/SendRequestFunctionType';

export enum GameClientInterceptorErrorTypes {
    AbortSendRequest = <any>'AbortSendRequest'
}

export class GameClientInterceptorError extends Error {
    public name = 'GameClientInterceptorError';

    constructor(public type : GameClientInterceptorErrorTypes, public message: string) {
        super(message);
    }

    public equalsTo(other: GameClientInterceptorError): boolean {
        if (other instanceof GameClientInterceptorError) {
            return this.name === other.name &&
                   this.type === other.type;
        }

        return false;
    }

    public static AbortSendRequest(message?: string): GameClientInterceptorError {
        return new GameClientInterceptorError(GameClientInterceptorErrorTypes.AbortSendRequest, message);
    }
}

export class GameClientInterceptorFactory {
    private static list: any = {};
    private static count = 0;

    public static register(component: any, ...args: any[]): IGameClientInterceptor {
        var instance: IGameClientInterceptor = null;
        var cmpName = component.name;
        if (cmpName === undefined || cmpName === '') {
            throw new Error('[GameClientInterceptorFactory] specified component does not contain property "name"');
        }

        var list = GameClientInterceptorFactory.list;
        var reg = list[cmpName];
        if (!reg) {
            instance = new component(...args);
            list[cmpName] = {
                name: cmpName,
                instance: instance
            };

            this.count++;

            if (DEBUG) {
                Logger.debugGroup('GameClientInterceptorFactory',
                    `Game client interceptor '${cmpName}' was successfully registered (${this.count} so far).`);
            }
        } else {
            throw new Error(`[GameClientInterceptorFactory] Game client interceptor "${cmpName}" is already registered!`);
        }

        return instance;
    }

    /**
     * Called before sending request.
     * @param context - request context with request (and related data) to be send.
     * @param sendRequestFn - function to send raw request
     */
    public static onSendGameRequest(context: InterceptorRequestContext, sendRequestFn: SendRequestFunctionType): Promise<any> {
        if (ObjectUtils.keyCount(GameClientInterceptorFactory.list) === 0) {
            return Promise.resolve();
        }

        var interceptorCount = 0;

        return Promise.resolve()
            .then(() => {
                let instances = lodash.map(GameClientInterceptorFactory.list, (value: any, key: string) => {
                    return {
                        name: key,
                        instance: value.instance
                    };
                }) as Array<any>;

                interceptorCount = instances.length;

                if (DEBUG) {
                    Logger.debugGroup('GameClientInterceptorFactory', `Executing event 'onSendGameRequest' for ${interceptorCount} interceptors...`);
                }

                return Promise.map(instances, item => {
                    let instanceName = item.name;
                    let instance = item.instance as IGameClientInterceptor;

                    return instance.onSendGameRequest(context, sendRequestFn)
                        .catch(err => {
                            if (DEBUG) {
                                Logger.errorGroup('GameClientInterceptorFactory', `[${instanceName}].onSendRequest failed: ` + Utils.getErrorMsg(err));
                            }
                        });
                });
            })
            .catch(err => {
                if (DEBUG) {
                    let errMsg = `Executing game client interceptors for event 'onSendRequest' throws error: ` + Utils.getErrorMsg(err);

                    if (err instanceof GameClientInterceptorError) {
                        errMsg = `Executing game client interceptors for event 'onSendRequest' was aborted, reason: ${err.type}`;
                    }

                    Logger.errorGroup('GameClientInterceptorFactory', errMsg);
                }
            })
            .finally(() => {
                if (DEBUG) {
                    Logger.debugGroup('GameClientInterceptorFactory', `Executing event 'onSendGameRequest' for ${interceptorCount} interceptors ends.`);
                }
            });
    }

    /**
     * Called when received error of send request.
     * @param context - request context with request (and related data) to be send.
     * @param clientError - client error
     */
    public static onRequestError(context: InterceptorRequestContext,
        clientError: ApiClientCommunication.Types.ApiClientError): void {

        if (ObjectUtils.keyCount(GameClientInterceptorFactory.list) === 0) {
            return;
        }

        let instances = lodash.map(GameClientInterceptorFactory.list, (value: any, key: string) => {
            return {
                name: key,
                instance: value.instance
            };
        }) as Array<any>;

        try {
            if (DEBUG) {
                Logger.debugGroup('GameClientInterceptorFactory', `Executing event 'onRequestError' for ${instances.length} interceptors...`);
            }

            _.each(instances, item => {
                let instanceName = item.name;
                let instance = item.instance as IGameClientInterceptor;

                try {
                    instance.onRequestError(context, clientError);
                }
                catch (err) {
                    if (DEBUG) {
                        Logger.debugGroup('GameClientInterceptorFactory', `[${instanceName}].onRequestError failed: ` + Utils.getErrorMsg(err));
                    }
                }
            });
        } catch (err) {
            if (DEBUG) {
                Logger.errorGroup('GameClientInterceptorFactory', `Executing game client interceptors for event 'onRequestError' throws error: ` + Utils.getErrorMsg(err));
            }
        } finally {
            if (DEBUG) {
                Logger.debugGroup('GameClientInterceptorFactory', `Executing event 'onRequestError' for ${instances.length} interceptors ends.`);
            }
        }
    }

    /**
     * Called when valid response was received from game server.
     * @param context - request context with request (and related data) to be send.
     * @param response - received response.
     */
    public static onResponseReceived<TResponse extends ApiClientCommunication.ResponseBase>(context: InterceptorRequestContext,
        response: TResponse): Promise<any> {

        if (ObjectUtils.keyCount(GameClientInterceptorFactory.list) === 0) {
            return Promise.resolve();
        }

        var interceptorCount = 0;

        return Promise.resolve()
            .then(() => {
                let instances = lodash.map(GameClientInterceptorFactory.list, (value: any, key: string) => {
                    return {
                        name: key,
                        instance: value.instance
                    };
                }) as Array<any>;

                interceptorCount = instances.length;

                if (DEBUG) {
                    Logger.debugGroup('GameClientInterceptorFactory', `Executing event 'onResponseReceived' for ${interceptorCount} interceptors...`);
                }

                return Promise.map(instances, item => {
                    let instanceName = item.name;
                    let instance = item.instance as IGameClientInterceptor;

                    return instance.onResponseReceived(context, response)
                        .catch(err => {
                            if (DEBUG) {
                                Logger.errorGroup('GameClientInterceptorFactory', `[${instanceName}].onResponseReceived failed: ` + Utils.getErrorMsg(err));
                            }
                        });
                });
            })
            .catch(err => {
                if (DEBUG) {
                    Logger.errorGroup('GameClientInterceptorFactory', `Executing game client interceptors for event 'onResponseReceived' throws error: `
                                                                      + Utils.getErrorMsg(err));
                }
            })
            .finally(() => {
                if (DEBUG) {
                    Logger.debugGroup('GameClientInterceptorFactory', `Executing event 'onResponseReceived' for ${interceptorCount} interceptors ends.`);
                }
            });
    }

    /**
     * Called when send game request promise ends regardless of success of failure (Bluebird method .finally() on promise).
     * @param {InterceptorRequestContext} context
     */
    public static onSendGameRequestFinal(context: InterceptorRequestContext): void {
        if (ObjectUtils.keyCount(GameClientInterceptorFactory.list) === 0) {
            return;
        }

        let instances = lodash.map(GameClientInterceptorFactory.list, (value: any, key: string) => {
            return {
                name: key,
                instance: value.instance
            };
        }) as Array<any>;

        try {
            if (DEBUG) {
                Logger.debugGroup('GameClientInterceptorFactory', `Executing event 'onSendGameRequestFinal' for ${instances.length} interceptors...`);
            }

            _.each(instances, item => {
                let instanceName = item.name;
                let instance = item.instance as IGameClientInterceptor;

                try {
                    instance.onSendGameRequestFinal(context);
                }
                catch (err) {
                    if (DEBUG) {
                        Logger.debugGroup('GameClientInterceptorFactory', `[${instanceName}].onSendGameRequestFinal failed: ` + Utils.getErrorMsg(err));
                    }
                }
            });
        } catch (err) {
            if (DEBUG) {
                Logger.errorGroup('GameClientInterceptorFactory', `Executing game client interceptors for event 'onSendGameRequestFinal' throws error: ` + Utils.getErrorMsg(err));
            }
        } finally {
            if (DEBUG) {
                Logger.debugGroup('GameClientInterceptorFactory', `Executing event 'onSendGameRequestFinal' for ${instances.length} interceptors ends.`);
            }
        }
    }

}

/**
 * @decorator gameClientInterceptor - registers interceptor for game client
 * @param component - implementation of @IGameClientInterceptor
 */
export function gameClientInterceptor() {
    return function (component: any) {
        GameClientInterceptorFactory.register(component);
        component.prototype.__componentName__ = component.name;
    };
}

const isBrowser = typeof window !== 'undefined';
if (isBrowser) {
    var win = window as any;
    win['gameClientInterceptor'] = gameClientInterceptor;
}


/** WEBPACK FOOTER **
 ** ./Engine/Communication/Game/GameClientInterceptorFactory.ts
 **/